// Typed payloads for the events increment 1 records (spec §2.8, chapter 4 §4.2–4.3).
// Each is schema version 1. Quantities are decimal strings with a UN/ECE Rec 20 unit
// and a recorded source (C2). Identifiers of goods are GS1 keys; internal ids are UUIDs
// generated by the client when a container comes into existence (D9-5).
import { z } from "zod";
import { isUuid } from "../ids.js";
import { ContainerIdentityScheme } from "../identity/scheme.js";
const Uuid = z.string().refine(isUuid, "must be a UUID");
const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD");
const Decimal = z.string().regex(/^-?\d+(\.\d+)?$/, "decimal string");
/** `standard`: the product register's standard quantity, shown and actively confirmed by the operator (owner 2026-11-03). */
export const QuantitySource = z.enum(["scanned", "inferred", "measured", "entered", "standard"]);
export const Quantity = z.object({
    value: Decimal,
    uom: z.string().min(1), // MTR, KGM, EA, ...
    source: QuantitySource,
});
/** "loose": a quantity of a product (and lot) on a location with no identity of its own — an
 *  ERP's opening balance, or goods another system put away (owner 2026-10-29). */
export const ContainerKind = z.enum(["trade-item", "logistic-unit", "loose"]);
export const PackagingLevelName = z.enum(["each", "inner", "case", "pallet"]);
export const LotData = z.object({
    number: z.string().min(1).max(20),
    bestBefore: IsoDate.nullable().default(null),
    expiry: IsoDate.nullable().default(null),
    productionDate: IsoDate.nullable().default(null),
    packagingDate: IsoDate.nullable().default(null),
});
/** Identity as scanned or assigned: the AI map plus the scheme-derived key. */
export const Identity = z.object({
    scheme: ContainerIdentityScheme,
    /** Canonical key from identityKey(); null when the carrier lacks a required AI (class level). */
    key: z.string().nullable(),
    ai: z.record(z.string()),
    /** "supplier" for carriers printed by the supplier, "tenant" for labels the system issued. */
    origin: z.enum(["supplier", "tenant"]),
});
export const ReceivedPayload = z
    .object({
    containerId: Uuid,
    kind: ContainerKind,
    identity: Identity,
    productId: Uuid.nullable().default(null),
    gtin: z.string().nullable().default(null),
    packagingLevel: PackagingLevelName.nullable().default(null),
    lot: LotData.nullable().default(null),
    serial: z.string().nullable().default(null),
    sscc: z.string().nullable().default(null),
    quantity: Quantity,
    locationId: Uuid,
    parentContainerId: Uuid.nullable().default(null),
    supplierPartyId: Uuid.nullable().default(null),
    expectedReceiptId: Uuid.nullable().default(null),
    deliveryId: Uuid.nullable().default(null),
    /** Stated (not observed) facts, e.g. from a founding import: locationStated=true. */
    stated: z.boolean().default(false),
})
    .strict();
export const IdentifiedPayload = z
    .object({
    containerId: Uuid,
    identity: Identity,
    /** reprint = same identifiers reprinted; supplement/replace = new identity linked to the old. */
    mode: z.enum(["commission", "reprint", "supplement", "replace"]),
    reason: z.string().nullable().default(null),
})
    .strict();
export const MovedPayload = z
    .object({
    /** Root containers moved; their subtrees follow (P11). */
    containerIds: z.array(Uuid).min(1),
    fromLocationId: Uuid.nullable().default(null),
    toLocationId: Uuid,
    proposedLocationId: Uuid.nullable().default(null),
    overrideReason: z.string().nullable().default(null),
})
    .strict();
export const AggregatedPayload = z
    .object({
    parentContainerId: Uuid,
    childContainerIds: z.array(Uuid).min(1),
    completeness: z.enum(["verified", "inferred"]).default("verified"),
})
    .strict();
export const DisaggregatedPayload = z
    .object({
    parentContainerId: Uuid,
    /** Specific children taken off, or "all". */
    childContainerIds: z.union([z.array(Uuid).min(1), z.literal("all")]),
})
    .strict();
/** Deviation dispositions — chapter 4 §4.5 catalogue (EPCIS-style dispositions, D4-2). */
export const Disposition = z.enum(["mismatch_quantity", "mismatch_class", "mismatch_instance", "unknown", "non_conformant", "damaged", "expired", "unavailable"]);
/** Inspected (observation): what was seen about a container, a pallet or a delivery — a
 *  deviation with its disposition and, where the operator confirmed it, the reason. A
 *  deviation is an event, never a silent fix (C4). */
export const InspectedPayload = z
    .object({
    /** The container the finding is about; null for a delivery-level finding. */
    containerId: Uuid.nullable().default(null),
    disposition: Disposition,
    /** Machine-readable detail: "unknown-product", "missing-lot", "quantity-unknown", "tally" … */
    detail: z.string().min(1).max(60),
    /** What was expected and seen, when the finding is a mismatch. */
    expected: z.union([z.number(), z.string()]).nullable().default(null),
    observed: z.union([z.number(), z.string()]).nullable().default(null),
    /** The operator's reason code or text, when one was given. */
    reason: z.string().max(500).nullable().default(null),
    gtin: z.string().nullable().default(null),
    lot: z.string().nullable().default(null),
    expectedReceiptId: Uuid.nullable().default(null),
    /** What the system did: recorded only, held the goods, or blocked the step. */
    action: z.enum(["recorded", "held", "blocked"]).default("recorded"),
})
    .strict();
/** Picked: containers taken for a customer order line (chapter 4 §4.3). The containers keep
 *  their place until they leave on the outbound pallet; the state says they are spoken for. */
export const PickedPayload = z
    .object({
    containerIds: z.array(Uuid).min(1),
    salesOrderId: Uuid,
    lineNo: z.number().int().positive(),
    /** The pallet the containers were put on, when known at the time of the scan. */
    palletContainerId: Uuid.nullable().default(null),
    /** Why a container other than the proposed one was taken, when the operator said. */
    overrideReason: z.string().max(500).nullable().default(null),
})
    .strict();
/** Split: a piece taken out of a container becomes a container of its own (owner 2026-10-28:
 *  the piece keeps the GTIN and gets the lot with a suffix, or a serial, per site setting).
 *  The parent keeps its identity with what remains. */
export const SplitPayload = z
    .object({
    parentContainerId: Uuid,
    childContainerId: Uuid,
    identity: Identity,
    productId: Uuid.nullable().default(null),
    gtin: z.string().nullable().default(null),
    lot: LotData.nullable().default(null),
    serial: z.string().nullable().default(null),
    /** What the piece holds. */
    quantity: Quantity,
    /** What the parent holds afterwards. */
    remaining: Quantity,
    locationId: Uuid.nullable().default(null),
    reason: z.string().max(500).nullable().default(null),
})
    .strict();
/** Merged: a bulk quantity (kind "loose", no identity) put where a row of the same product already
 *  stands joins it (owner 2026-11-03: one row per product and location). The rows named in `from` end
 *  (state merged, quantity 0); `into` holds the sum. The projector sums the rows it has, `quantity` is
 *  what the phone saw. */
export const MergedPayload = z
    .object({
    intoContainerId: Uuid,
    fromContainerIds: z.array(Uuid).min(1),
    fromLocationId: Uuid.nullable().default(null),
    toLocationId: Uuid,
    quantity: z.object({ value: z.string(), uom: z.string() }),
})
    .strict();
/** Packed: an outbound logistic unit comes into existence (the pallet a pick is built on). */
export const PackedPayload = z
    .object({
    containerId: Uuid,
    identity: Identity,
    sscc: z.string().regex(/^\d{18}$/),
    locationId: Uuid.nullable().default(null),
    salesOrderId: Uuid.nullable().default(null),
})
    .strict();
/** Payload schemas by event type (version 1). Types not listed yet accept any object. */
/** Counted (observation): a location was counted whole — what was scanned, what Trail expected
 *  and did not see, stated quantities (split cartons, or everything by site rule), and the round.
 *  Nothing changes in stock; the lead's approval turns missing into Adjusted (mockup counting-v1). */
export const CountedPayload = z
    .object({
    locationId: Uuid,
    containerIds: z.array(Uuid),
    missingContainerIds: z.array(Uuid).default([]),
    quantities: z.array(z.object({ containerId: Uuid, value: z.string(), uom: z.string() })).default([]),
    roundId: Uuid.nullable().default(null),
    /** The location was said to be empty (zero-point at pick, or counted and nothing there). */
    empty: z.boolean().default(false),
})
    .strict();
/** Adjusted (physical): a container's quantity set by a count; "0" takes it out of stock. The ERP
 *  adapter reports adjustments per lot. */
export const AdjustedPayload = z
    .object({
    containerId: Uuid,
    quantity: z.object({ value: z.string(), uom: z.string() }),
    previous: z.object({ value: z.string(), uom: z.string() }).nullable().default(null),
    reason: z.string().max(500),
    roundId: Uuid.nullable().default(null),
    countedEventId: Uuid.nullable().default(null),
})
    .strict();
/** Reversed: the ERP (or the office) took a recorded step back — today a pick the ERP undid
 *  (owner 2026-11-02). The containers named are stock again where they stand; the order is open. */
export const ReversedPayload = z
    .object({
    of: z.enum(["pick"]),
    salesOrderId: Uuid,
    reference: z.string().min(1),
    containerIds: z.array(Uuid),
    palletContainerIds: z.array(Uuid).default([]),
    source: z.string().min(1),
    reason: z.string().max(1000),
})
    .strict();
/** The goods left the building: the order's picked cartons and pallets. `source` says who knew — the
 *  loader at the dock, or the ERP realising the order (owner 2026-11-02: Visma wins, Trail follows). */
export const ShippedPayload = z
    .object({
    salesOrderId: Uuid.nullable().default(null),
    reference: z.string().nullable().default(null),
    containerIds: z.array(Uuid),
    palletContainerIds: z.array(Uuid).default([]),
    source: z.string().min(1),
    reason: z.string().max(1000).nullable().default(null),
})
    .strict();
/** A pallet retired because nothing is left on it (owner 2026-11-02: it may still exist physically,
 *  but in Trail it is over — scanning it as a destination says so, a new pallet gets a new number). */
export const EmptiedPayload = z
    .object({
    containerIds: z.array(Uuid).min(1),
    source: z.enum(["auto", "manual"]),
    reason: z.string().max(1000).nullable().default(null),
})
    .strict();
export const PAYLOAD_SCHEMAS = {
    reversed: ReversedPayload,
    shipped: ShippedPayload,
    emptied: EmptiedPayload,
    counted: CountedPayload,
    adjusted: AdjustedPayload,
    picked: PickedPayload,
    split: SplitPayload,
    merged: MergedPayload,
    packed: PackedPayload,
    received: ReceivedPayload,
    founding_count: ReceivedPayload,
    identified: IdentifiedPayload,
    inspected: InspectedPayload,
    moved: MovedPayload,
    aggregated: AggregatedPayload,
    disaggregated: DisaggregatedPayload,
};
export function parsePayload(type, payload) {
    return PAYLOAD_SCHEMAS[type].parse(payload);
}
//# sourceMappingURL=payloads.js.map